import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { deleteApiKeys, getAccount, getApiKeys, getBillingCard, recreateApiKeys } from "src/services/accountService";
import { LocalStorage } from "src/services/localStorageService";
import PaymentService from "src/services/paymentService";
import LanguageSwitcher from "src/ui/containers/account/SettingsPage/Components/LanguageSwitcher";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import AuthService from "@services/authService";
import { getSettings } from "@services/configService";
import ApmLink from "@shared/ApmLink";
import { DangerButton, DangerOutlinedButton, PrimaryOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { type PageContentWidth, PageContentWrapper } from "@shared/PageContentWrapper/PageContentWrapper";
import { PageTabList } from "@shared/PageTabList/PageTabList";
import { SectionField, SectionFields, SectionFormGrid } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

import { PasswordChangeForm } from "./ChangePassword/PasswordChangeForm";
import EditStripeCard from "./Components/EditStripeCard";
import { TenantSwitcher } from "./Components/TenantSwitcher";
import { ThemeSwitcher } from "./Components/ThemeSwitcher";
import { DeleteAccountForm } from "./DeleteAccount/DeleteAccountForm";
import { BillingEditForm } from "./EditBilling/BillingEditForm";
import { ProfileEditForm } from "./EditProfile/ProfileEditForm";
import { TwoFactorDisableForm } from "./TwoFactorAuthorization/TwoFactorDisableForm";
import { TwoFactorEnableForm } from "./TwoFactorAuthorization/TwoFactorEnableForm";
import { useStyles } from "./SettingsPage.style";

const mediumWidthTabs = ["apikey", "security", "billing"] as const;

const getPageContentWidth = (tab: string): PageContentWidth =>
    mediumWidthTabs.includes(tab as (typeof mediumWidthTabs)[number]) ? "medium" : "large";

const getAppVersion = () => {
    const meta = document.querySelector("meta[name='version']");
    return meta?.getAttribute("content");
};

function displayValue(value: string | null | undefined) {
    return value?.trim() ? value : "-";
}

function capitalize(value: string | null | undefined) {
    if (!value?.trim()) {
        return "-";
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
}

const SettingsPage = () => {
    const { t } = useTranslation(["common", "pages"]);
    const [profile, setProfile] = useState<API.Profile>();
    const [billingAddress, setBillingAddress] = useState<API.BillingAddress | undefined>();
    const [billingCard, setBillingCard] = useState<API.BillingCard | null>(null);

    const [apiKeys, setApiKeys] = useState<string | undefined>();
    const [isRemovingApiKey, setIsRemovingApiKey] = useState(false);
    const [isRenewApiKey, setIsRenewApiKey] = useState(false);
    const [isModalShown, setIsModalShown] = useState(false);
    const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
    const [stripePromise, setStripePromise] = useState<PromiseLike<Stripe | null> | Stripe | null>(null);

    const { features } = getSettings();
    const isStripeEnabled = features.stripeEnabled;

    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get("tab");
    const modeFromUrl = searchParams.get("mode");
    const validTabs = ["portal", "profile", "security", "billing", "apikey"];
    const initialTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "portal";
    const [activeTab, setActiveTab] = useState(initialTab);

    const handleTabChange = useCallback(
        (_: React.SyntheticEvent, value: string) => {
            setActiveTab(value);
            setSearchParams({ tab: value }, { replace: true });
        },
        [setSearchParams],
    );

    const clearMode = useCallback(() => {
        setSearchParams({ tab: activeTab }, { replace: true });
    }, [activeTab, setSearchParams]);

    const loadBillingData = useCallback(async () => {
        try {
            const { data } = await getAccount();
            setProfile(data.profile);
            setBillingAddress(data?.billingAddress);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }

        try {
            const apiKeysData = await getApiKeys();
            setApiKeys(apiKeysData);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    }, []);

    const handleProfileEditSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const handleBillingEditSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const handleTwoFactorSuccess = useCallback(() => {
        clearMode();
        void loadBillingData();
    }, [clearMode, loadBillingData]);

    const { classes } = useStyles();

    const copyToClipboard = useCallback((text: string | undefined) => {
        if (!text) {
            return;
        }
        const sanitizedText = text.trim();
        navigator.clipboard
            .writeText(sanitizedText)
            .then(() => {
                showSuccessToast("Copied to clipboard");
            })
            .catch((_error) => {
                showErrorToast("Copy failed");
            });
    }, []);

    const handleApiKeysRenew = async () => {
        setIsRenewApiKey(true);

        try {
            const { model, message } = await recreateApiKeys();
            setApiKeys(model);
            showSuccessToast(message);
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRenewApiKey(false);
        }
    };
    const handleApiKeysDeletion = async () => {
        setIsRemovingApiKey(true);

        try {
            const { message } = await deleteApiKeys();
            showSuccessToast(message);
            setApiKeys("");
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        } finally {
            setIsRemovingApiKey(false);
        }
    };

    const handleResetSettings = () => {
        LocalStorage.reset();
        showSuccessToast("Portal settings have been reset to default");
        window.location.reload();
    };
    const handleResetConfirm = () => {
        handleResetSettings();
    };

    useEffect(() => {
        const loadCardData = async () => {
            try {
                const _billingCard = await getBillingCard();
                setBillingCard(_billingCard);
                if (_billingCard !== null) {
                    const promiseData = await PaymentService.getStripePromise(loadStripe);
                    setStripePromise(promiseData as PromiseLike<Stripe | null> | Stripe | null);
                }
            } catch (_error) {
                showErrorToast("Failed retrieving billing card");
            }
        };
        if (isStripeEnabled) {
            void loadCardData();
        }
    }, [isStripeEnabled]);

    useEffect(() => {
        void loadBillingData();
    }, [loadBillingData]);

    useEffect(() => {
        if (tabFromUrl && validTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl, activeTab]);

    if (!profile) {
        return null;
    }

    return (
        <PageContainer className={classes.container}>
            <PageContentWrapper size={getPageContentWidth(activeTab)}>
                <TabContext value={activeTab}>
                    <PageTabList onChange={handleTabChange}>
                        <Tab
                            value="portal"
                            label={t("pages:settingsPage.portalSettings.title")}
                            icon={<i className="fa-solid fa-sliders" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="profile"
                            label={t("pages:settingsPage.profileData.title")}
                            icon={<i className="fa-solid fa-user" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="security"
                            label={t("pages:settingsPage.accountSecurity.title")}
                            icon={<i className="fa-solid fa-shield-halved" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="billing"
                            label={t("pages:settingsPage.billingAddress.title")}
                            icon={<i className="fa-solid fa-credit-card" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="apikey"
                            label={t("pages:settingsPage.apiKey.title")}
                            icon={<i className="fa-solid fa-key" />}
                            iconPosition="start"
                        />
                    </PageTabList>

                    <TabPanel value="portal" className={classes.tabPanel}>
                        <SectionPanel
                            title={t("pages:settingsPage.portalSettings.title")}
                            actions={
                                <DangerOutlinedButton
                                    size="small"
                                    startIcon={<i className="fas fa-fw fa-undo" />}
                                    onClick={() => setIsResetConfirmOpen(true)}
                                    name="reset-settings"
                                >
                                    {t("pages:settingsPage.portalSettings.resetSettings.button")}
                                </DangerOutlinedButton>
                            }
                        >
                            <Box className={classes.settingsRows}>
                                <Box className={classes.settingsRow}>
                                    <Box className={classes.switchContent}>
                                        <span className={classes.switchLabel}>{t("pages:settingsPage.language.label")}</span>
                                        <div>{t("pages:settingsPage.language.description")}</div>
                                    </Box>
                                    <Box className={classes.switcherBox}>
                                        <LanguageSwitcher showLabel={false} fullWidth={true} />
                                    </Box>
                                </Box>

                                <Box className={classes.settingsRow}>
                                    <Box className={classes.switchContent}>
                                        <span className={classes.switchLabel}>
                                            {t("pages:settingsPage.portalSettings.themeSwitch.label")}
                                        </span>
                                        <div>{t("pages:settingsPage.portalSettings.themeSwitch.description")}</div>
                                    </Box>
                                    <Box className={classes.switcherBox}>
                                        <ThemeSwitcher />
                                    </Box>
                                </Box>

                                {AuthService.mayUserSeeTenantSwitcher() ? (
                                    <Box className={classes.settingsRow}>
                                        <Box className={classes.switchContent}>
                                            <span className={classes.switchLabel}>
                                                {t("pages:settingsPage.portalSettings.tenantSwitch.label")}
                                            </span>
                                            <div>{t("pages:settingsPage.portalSettings.tenantSwitch.description")}</div>
                                        </Box>
                                        <Box className={classes.switcherBox}>
                                            <TenantSwitcher />
                                        </Box>
                                    </Box>
                                ) : null}
                            </Box>
                        </SectionPanel>
                    </TabPanel>

                    <TabPanel value="profile" className={classes.tabPanel}>
                        {modeFromUrl === "edit" ? (
                            <ProfileEditForm profile={profile!} onSuccess={handleProfileEditSuccess} onCancel={clearMode} />
                        ) : modeFromUrl === "delete" ? (
                            <DeleteAccountForm onCancel={clearMode} />
                        ) : (
                            <SectionPanel
                                title={t("pages:settingsPage.profileData.title")}
                                actions={
                                    <>
                                        <Link to="/account/settings?tab=profile&mode=edit">
                                            <PrimaryOutlinedButton
                                                size="small"
                                                startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                                name="edit-profile"
                                            >
                                                {t("common:button.edit")}
                                            </PrimaryOutlinedButton>
                                        </Link>
                                        <Link to="/account/settings?tab=profile&mode=delete">
                                            <DangerOutlinedButton
                                                size="small"
                                                startIcon={<i className="fas fa-fw fa-trash-alt" />}
                                                name="delete-profile"
                                            >
                                                {t("pages:settingsPage.profileData.delete")}
                                            </DangerOutlinedButton>
                                        </Link>
                                    </>
                                }
                            >
                                <SectionFormGrid>
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.email")}
                                        value={displayValue(profile?.email)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.authentication")}
                                        value={
                                            profile?.twoFactorEnabled
                                                ? t("pages:settingsPage.profileData.twoFactor")
                                                : t("common:label.password")
                                        }
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.name")}
                                        value={displayValue(profile?.name)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.phone")}
                                        value={displayValue(`${profile?.phoneCode ?? ""}${profile?.phoneNumber ?? ""}`)}
                                    />
                                    <SectionField
                                        label={t("common:label.address")}
                                        value={displayValue(profile?.address)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.city")}
                                        value={capitalize(profile?.city)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.zipCode")}
                                        value={displayValue(profile?.zipCode)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.country")}
                                        value={displayValue(profile?.country)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.notifications")}
                                        value={
                                            profile?.newsletterChecked
                                                ? t("pages:settingsPage.profileData.extended")
                                                : t("pages:settingsPage.profileData.standard")
                                        }
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.usage")}
                                        value={
                                            profile?.typeOfUsage === "b"
                                                ? t("pages:settingsPage.profileData.businessUse")
                                                : t("pages:settingsPage.profileData.nonBusinessUse")
                                        }
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.company")}
                                        value={displayValue(profile?.companyName)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.industry")}
                                        value={displayValue(profile?.fieldOfApplication)}
                                    />
                                </SectionFormGrid>
                            </SectionPanel>
                        )}
                    </TabPanel>

                    <TabPanel value="security" className={classes.tabPanel}>
                        {modeFromUrl === "password" ? (
                            <PasswordChangeForm onCancel={clearMode} />
                        ) : modeFromUrl === "2fa-enable" ? (
                            <TwoFactorEnableForm onSuccess={handleTwoFactorSuccess} onCancel={clearMode} />
                        ) : modeFromUrl === "2fa-disable" ? (
                            <TwoFactorDisableForm onSuccess={handleTwoFactorSuccess} onCancel={clearMode} />
                        ) : (
                            <SectionPanel title={t("pages:settingsPage.accountSecurity.title")}>
                                <Box className={classes.settingsRows}>
                                    <Box className={classes.settingsRow}>
                                        <Box className={classes.switchContent}>
                                            <span className={classes.switchLabel}>
                                                {t("pages:settingsPage.accountSecurity.accountPassword")}
                                            </span>
                                            <div>{t("pages:settingsPage.accountSecurity.accountPasswordDescription")}</div>
                                        </Box>
                                        <ApmLink
                                            parent="Account security settings"
                                            title={t("common:button.change")}
                                            role="button"
                                            to="/account/settings?tab=security&mode=password"
                                        >
                                            <PrimaryOutlinedButton
                                                size="small"
                                                startIcon={<i className="fa-solid fa-fw fa-key" />}
                                            >
                                                {t("common:button.change")}
                                            </PrimaryOutlinedButton>
                                        </ApmLink>
                                    </Box>
                                    <Box className={classes.settingsRow}>
                                        <Box className={classes.switchContent}>
                                            <span className={classes.switchLabel}>
                                                {t("pages:settingsPage.accountSecurity.twoFactor")}
                                            </span>
                                            <div>{t("pages:settingsPage.accountSecurity.twoFactorDescription")}</div>
                                        </Box>
                                        {profile?.twoFactorEnabled ? (
                                            <ApmLink
                                                parent="Account security settings"
                                                title={t("common:button.disable")}
                                                role="button"
                                                to="/account/settings?tab=security&mode=2fa-disable"
                                            >
                                                <SecondaryButton size="small">{t("common:button.disable")}</SecondaryButton>
                                            </ApmLink>
                                        ) : (
                                            <ApmLink
                                                parent="Account security settings"
                                                title={t("common:button.enable")}
                                                role="button"
                                                to="/account/settings?tab=security&mode=2fa-enable"
                                            >
                                                <PrimaryOutlinedButton
                                                    size="small"
                                                    startIcon={<i className="fa-solid fa-shield-halved" />}
                                                >
                                                    {t("common:button.enable")}
                                                </PrimaryOutlinedButton>
                                            </ApmLink>
                                        )}
                                    </Box>
                                </Box>
                            </SectionPanel>
                        )}
                    </TabPanel>

                    <TabPanel value="billing" className={classes.tabPanelBilling}>
                        {modeFromUrl === "edit" ? (
                            <BillingEditForm
                                billingAddress={billingAddress}
                                onSuccess={handleBillingEditSuccess}
                                onCancel={clearMode}
                            />
                        ) : (
                            <SectionPanel
                                title={t("pages:settingsPage.billingAddress.title")}
                                actions={
                                    <Link to="/account/settings?tab=billing&mode=edit">
                                        <PrimaryOutlinedButton
                                            size="small"
                                            startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                            name="edit-billing-address"
                                        >
                                            {t("common:button.edit")}
                                        </PrimaryOutlinedButton>
                                    </Link>
                                }
                            >
                                <SectionFields>
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.name")}
                                        value={displayValue(billingAddress?.name)}
                                    />
                                    <SectionField
                                        label={t("common:label.address")}
                                        value={displayValue(billingAddress?.address)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.city")}
                                        value={displayValue(billingAddress?.city)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.zipCode")}
                                        value={displayValue(billingAddress?.zipCode)}
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.profileData.country")}
                                        value={displayValue(billingAddress?.country)}
                                    />
                                </SectionFields>
                            </SectionPanel>
                        )}
                        {isStripeEnabled ? (
                            <SectionPanel
                                title={t("pages:settingsPage.paymentMethod.title")}
                                actions={
                                    <Tooltip placement="top" title={t("pages:settingsPage.paymentMethod.editTooltip")}>
                                        <PrimaryOutlinedButton
                                            startIcon={<i className="fa-solid fa-fw fa-pencil" />}
                                            onClick={() => setIsModalShown(true)}
                                            size="small"
                                            disabled
                                            name="edit-payment-method"
                                        >
                                            {t("common:button.edit")}
                                        </PrimaryOutlinedButton>
                                    </Tooltip>
                                }
                            >
                                <SectionFields>
                                    <SectionField
                                        label={t("pages:settingsPage.paymentMethod.cardType")}
                                        value={
                                            billingCard?.brand
                                                ? capitalize(billingCard.brand)
                                                : t("pages:settingsPage.paymentMethod.noCardSet")
                                        }
                                    />
                                    <SectionField
                                        label={t("pages:settingsPage.paymentMethod.cardNumber")}
                                        value={
                                            billingCard?.lastFourDigits
                                                ? `**** **** **** ${billingCard.lastFourDigits}`
                                                : t("pages:settingsPage.paymentMethod.noCardNumber")
                                        }
                                    />
                                    <SectionField
                                        label={t("common:label.expirationDate")}
                                        value={`${billingCard?.expMonth ?? "MM"} / ${billingCard?.expYear ?? "YY"}`}
                                    />
                                </SectionFields>
                            </SectionPanel>
                        ) : null}
                        {stripePromise && isStripeEnabled ? (
                            <Elements stripe={stripePromise}>
                                <EditStripeCard open={isModalShown} onClose={() => setIsModalShown(false)} />
                            </Elements>
                        ) : null}
                    </TabPanel>

                    <TabPanel value="apikey" className={classes.tabPanel}>
                        <SectionPanel title={t("pages:settingsPage.apiKey.title")}>
                            <Box className={classes.apiKeyContainer}>
                                {apiKeys ? (
                                    <>
                                        <span>{apiKeys}</span>
                                        <div className={classes.apiKeyActions}>
                                            <SecondaryButton
                                                startIcon={<i className="fa-solid fa-copy" />}
                                                name="copy-api-key"
                                                onClick={() => copyToClipboard(apiKeys)}
                                            >
                                                {t("common:button.copy")}
                                            </SecondaryButton>
                                            <SecondaryButton
                                                startIcon={<i className="fa-solid fa-arrow-rotate-left" />}
                                                name="renew-api-key"
                                                onClick={() => handleApiKeysRenew()}
                                                loading={isRenewApiKey}
                                                disabled={isRenewApiKey || isRemovingApiKey}
                                                size="small"
                                            >
                                                {t("pages:settingsPage.apiKey.renew")}
                                            </SecondaryButton>
                                            <DangerButton
                                                disabled={!apiKeys || isRemovingApiKey || isRenewApiKey}
                                                size="small"
                                                startIcon={<i className="fas fa-fw fa-trash-alt" />}
                                                name="delete-api-key"
                                                onClick={() => handleApiKeysDeletion()}
                                                loading={isRemovingApiKey}
                                            >
                                                {t("common:button.delete")}
                                            </DangerButton>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <span>{t("pages:settingsPage.apiKey.noKey")}</span>
                                        <PrimaryOutlinedButton
                                            startIcon={<i className="fa-solid fa-key" />}
                                            onClick={() => handleApiKeysRenew()}
                                            loading={isRenewApiKey}
                                            disabled={isRenewApiKey || isRemovingApiKey}
                                            size="small"
                                            name="generate-api-key"
                                        >
                                            {t("common:button.generate")}
                                        </PrimaryOutlinedButton>
                                    </>
                                )}
                            </Box>
                        </SectionPanel>
                    </TabPanel>
                </TabContext>
            </PageContentWrapper>

            <Box className={classes.versionDisplay}>
                {t("pages:settingsPage.version")}: {getAppVersion()}
            </Box>

            <ConfirmDialog
                open={isResetConfirmOpen}
                onClose={() => setIsResetConfirmOpen(false)}
                onConfirm={handleResetConfirm}
                title={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.title")}
                primaryActionText={t("pages:settingsPage.portalSettings.resetSettings.confirmModal.confirmButton")}
                dangerButton
            >
                <Box component="p" className={classes.confirmDialogParagraph}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.description")}
                </Box>
                <Box component="p" className={classes.confirmDialogListTitle}>
                    {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeReset")}
                </Box>
                <Box component="ul" className={classes.confirmDialogList}>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.languageAndTheme")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.uiPreferences")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.tableSettings")}
                    </li>
                    <li>
                        {t("pages:settingsPage.portalSettings.resetSettings.confirmModal.whatWillBeResetItems.collapsedSections")}
                    </li>
                </Box>
            </ConfirmDialog>
        </PageContainer>
    );
};

export default withMetaDescription(SettingsPage, {
    title: i18next.t("pages:settingsPage.metaTitle"),
    description: i18next.t("pages:settingsPage.metaDescription"),
});
